import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { RadarChart, PolarGrid, PolarAngleAxis, Radar, ResponsiveContainer } from "recharts";
import { Sparkles, Trophy, Target, Loader2, ArrowRight, Lightbulb } from "lucide-react";

const LABELS = {
  opening: "Opening", confidence: "Confidence", rapport: "Rapport",
  questions: "Questions", listening: "Listening", objection_handling: "Objections",
  closing: "Closing", adaptability: "Adaptability", sales_psychology: "Psychology",
};

export default function Share() {
  const { id } = useParams();
  const [data, setData] = useState(undefined);

  useEffect(() => {
    api.get(`/share/${id}`).then((r) => setData(r.data)).catch(() => setData(null));
  }, [id]);

  if (data === undefined) return <div className="min-h-screen flex items-center justify-center text-emerald-600" style={{ background: "#F4F7FB" }}><Loader2 className="w-6 h-6 animate-spin" /></div>;

  const radar = data ? Object.entries(data.scores || {}).map(([k, v]) => ({ k: LABELS[k] || k, v })) : [];

  return (
    <div className="min-h-screen grid-bg text-slate-900" style={{ background: "#F4F7FB" }} data-testid="share-page">
      <header className="max-w-3xl mx-auto px-6 pt-8 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="share-home-link">
          <div className="w-9 h-9 rounded-xl bg-emerald-500 flex items-center justify-center glow-emerald"><Sparkles className="w-5 h-5 text-black" /></div>
          <span className="font-display font-extrabold">Pitch Craft</span>
        </Link>
        <Link to="/register"><Button size="sm" className="bg-emerald-500 hover:bg-emerald-600 text-black font-semibold" data-testid="share-cta-top">Try it free</Button></Link>
      </header>

      <main className="max-w-3xl mx-auto px-6 py-10 space-y-6">
        {!data ? (
          <div className="card-surface rounded-3xl p-10 text-center" data-testid="share-missing">
            <h1 className="text-2xl font-bold">This conversation isn't available</h1>
            <p className="text-slate-500 mt-2">The link may have expired or been removed by its owner.</p>
          </div>
        ) : (
          <>
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="card-surface rounded-3xl p-6 sm:p-8">
              <p className="text-emerald-600 font-mono text-xs uppercase tracking-widest">Shared roleplay · {data.channel_label || "Sales practice"}</p>
              <div className="flex items-center gap-4 mt-3 flex-wrap">
                <img src={data.persona.avatar} alt="" className="w-16 h-16 rounded-full object-cover object-top border-2 border-white shadow" />
                <div className="min-w-0">
                  <h1 className="text-2xl sm:text-3xl font-extrabold tracking-tight" data-testid="share-title">{data.rep_name} vs {data.persona.name}</h1>
                  <p className="text-slate-500 text-sm">{data.persona.title}{data.date ? ` · ${new Date(data.date).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" })}` : ""}</p>
                </div>
                <div className="ml-auto flex items-center gap-3">
                  <div className="text-center">
                    <div className="text-4xl font-extrabold text-emerald-600" data-testid="share-rating">{data.rating ?? "–"}</div>
                    <div className="text-[10px] font-mono uppercase tracking-wider text-slate-500">Score</div>
                  </div>
                  <span className={`px-3 py-1.5 rounded-full text-xs font-semibold border ${data.deal_won ? "bg-emerald-500/10 text-emerald-700 border-emerald-500/30" : "bg-slate-100 text-slate-600 border-slate-200"}`} data-testid="share-deal">
                    {data.deal_won ? <><Trophy className="w-3 h-3 inline mr-1" />Deal won</> : "No deal — yet"}
                  </span>
                </div>
              </div>
              {data.summary && <p className="text-slate-700 mt-5 leading-relaxed border-l-4 border-emerald-400 pl-4 italic" data-testid="share-summary">"{data.summary}"</p>}
            </motion.div>

            <div className="grid md:grid-cols-2 gap-4">
              <div className="card-surface rounded-2xl p-5">
                <h2 className="font-semibold flex items-center gap-2"><Target className="w-4 h-4 text-emerald-600" /> Skill breakdown</h2>
                <div className="h-56 mt-2">
                  <ResponsiveContainer width="100%" height="100%">
                    <RadarChart data={radar} outerRadius="75%">
                      <PolarGrid stroke="#E2E8F0" />
                      <PolarAngleAxis dataKey="k" tick={{ fill: "#64748B", fontSize: 10 }} />
                      <Radar dataKey="v" stroke="#10B981" fill="#10B981" fillOpacity={0.35} />
                    </RadarChart>
                  </ResponsiveContainer>
                </div>
              </div>
              <div className="card-surface rounded-2xl p-5 space-y-4">
                <div>
                  <h3 className="text-xs font-mono uppercase tracking-wider text-emerald-600">Strengths</h3>
                  <ul className="mt-2 space-y-1.5 text-sm text-slate-700">{data.strengths.map((s, i) => <li key={`${i}-${s.slice(0, 24)}`} className="flex gap-2"><span className="text-emerald-500">✓</span>{s}</li>)}</ul>
                </div>
                <div>
                  <h3 className="text-xs font-mono uppercase tracking-wider text-amber-600 flex items-center gap-1"><Lightbulb className="w-3 h-3" /> Missed opportunities</h3>
                  <ul className="mt-2 space-y-1.5 text-sm text-slate-700">{data.missed_opportunities.map((s, i) => <li key={`${i}-${s.slice(0, 24)}`} className="flex gap-2"><span className="text-amber-500">•</span>{s}</li>)}</ul>
                </div>
              </div>
            </div>

            <div className="card-surface rounded-2xl p-5" data-testid="share-transcript">
              <h2 className="font-semibold">The conversation</h2>
              <div className="mt-4 space-y-3">
                {data.messages.map((m, i) => (
                  <div key={`${i}-${m.role}-${(m.content || "").slice(0, 16)}`} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.role === "user" ? "bg-emerald-500 text-black" : "bg-slate-100 text-slate-800"}`}>
                      <div className="text-[10px] font-mono uppercase tracking-wider opacity-60 mb-0.5">{m.role === "user" ? data.rep_name : data.persona.name}</div>
                      {m.content.replace(/\*[^*]*\*/g, "").trim()}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <div className="rounded-3xl p-8 text-center bg-slate-900 text-white">
              <h2 className="text-xl sm:text-2xl font-extrabold">Think you'd have closed {data.persona.name}?</h2>
              <p className="text-slate-300 mt-2 text-sm">Practice unlimited realistic sales conversations with AI customers and get coached after every call.</p>
              <Link to="/register"><Button className="mt-5 bg-emerald-500 hover:bg-emerald-400 text-black font-semibold h-11 px-6" data-testid="share-cta-bottom">Try Pitch Craft free <ArrowRight className="w-4 h-4 ml-1.5" /></Button></Link>
            </div>
          </>
        )}
      </main>
    </div>
  );
}
